import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import type { ImplementationRecord } from "../../../services/implementationRecordService";
import MarkdownContent from "../../../utils/renderMarkdown";
import { getAiHubStatusChipSx, type AiHubStatusTone } from "../aiHubChipStyles";
import {
  assessorInitials,
  formatImpactLevelChip,
  formatImpactedLevelLabel,
} from "./implementationRecordView";

const AVATAR_COLORS = ["#2563EB", "#0F766E", "#C2410C", "#7C3AED", "#B45309"];

const CONTENT_BG = "rgba(242, 242, 242, 0.45)";

function avatarColor(name: string): string {
  let hash = 0;
  for (const ch of name) hash = (hash + ch.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash] ?? "#2563EB";
}

function impactLevelTone(label: string): AiHubStatusTone {
  if (label === "High") return "red";
  if (label === "Medium") return "yellow";
  return "blue";
}

export function ImpactStatusChips({
  impacted,
  impactedLevel,
}: Readonly<{
  impacted: boolean;
  impactedLevel: string | null | undefined;
}>) {
  const levelChip = formatImpactLevelChip(impactedLevel);
  const levelLabel = formatImpactedLevelLabel(impactedLevel);
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {impacted ? (
        <Chip size="small" label="Impacted" sx={getAiHubStatusChipSx("red")} />
      ) : (
        <Chip size="small" label="No impact" sx={getAiHubStatusChipSx("green")} />
      )}
      {impacted && levelChip ? (
        <Chip
          size="small"
          label={levelChip}
          sx={getAiHubStatusChipSx(impactLevelTone(levelLabel))}
        />
      ) : null}
    </Box>
  );
}

type ImplementationRecordDetailContentProps = Readonly<{
  record: ImplementationRecord;
}>;

/**
 * Shared assessment body used by the list Details dialog and Assessment Pack
 * detail view — everything below the outer "Details" chrome.
 */
export default function ImplementationRecordDetailContent({
  record,
}: ImplementationRecordDetailContentProps) {
  const assessorLabel = record.assessorName || record.assessor || "Assessor";

  return (
    <Box
      sx={{
        bgcolor: CONTENT_BG,
        borderRadius: "6px",
        p: "20px 22px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "999px",
            bgcolor: avatarColor(assessorLabel),
            color: "#fff",
            fontSize: 11,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          {assessorInitials(assessorLabel)}
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#101828" }}>
          {assessorLabel}
        </Typography>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <ImpactStatusChips
          impacted={record.impacted}
          impactedLevel={record.impactedLevel}
        />
      </Box>

      <Typography
        component="h3"
        sx={{ fontSize: 14, fontWeight: 700, color: "#101828", mb: 1 }}
      >
        Summary
      </Typography>
      <MarkdownContent markdown={record.assessmentSummary} />

      <Typography
        component="h3"
        sx={{ fontSize: 14, fontWeight: 700, color: "#101828", mt: 2.5, mb: 1 }}
      >
        Analysis
      </Typography>
      <MarkdownContent markdown={record.detailedAnalysis} />

      <Typography
        component="h3"
        sx={{ fontSize: 14, fontWeight: 700, color: "#101828", mt: 2.5, mb: 1 }}
      >
        Recommended Actions
      </Typography>
      <MarkdownContent markdown={record.recommendation} />
    </Box>
  );
}
